import { UntypedFormGroup } from '@angular/forms';
import { IErrorFieldObject } from '@app/models/error-fields.model';
import * as moment from 'moment';
import { FORM_CONTROL_STATUS_DISABLED } from '../constants/globalConstants';
import { eProductNames } from '@app/models/fi-products.model';
import { IBorrowerCoBorrowerAge } from '@app/models/borrower.model';
import { PaymentProtectionRate } from '@app/models/rate-response.model';
import { CoverageName } from '@app/models/Coverage-Name';

/**
 * Given date as a string, add given number of days to it
 * @param date in YYYY-MM-DD format
 * @param days number
 * @returns string - Date in YYYY-MM-DD format
 */
export function addDays(date: string, days: number): string {
  return moment(date, 'YYYY-MM-DD').add(days, 'days').format('YYYY-MM-DD');
}
/**
 * Returns formatted phone number (Cleans spaces and format)
 * @param phoneNumber
 * @returns formattedPhoneNumber
 */
export function getFormattedPhoneNumber(phoneNumber: string): string {
  const cleanedNumber = ('' + phoneNumber).replace(/\D/g, '');
  if (cleanedNumber.length === 10) {
    const match = cleanedNumber.match(/^(\d{3})(\d{3})(\d{4})$/);
    let formattedPhoneNumber: string = '';
    if (match) {
      formattedPhoneNumber = '(' + match[1] + ')' + match[2] + '-' + match[3];
    }
    return formattedPhoneNumber;
  } else {
    return cleanedNumber;
  }
}

/**
 * Utility to copy source object into target object including missing properties in target
 */
/**
 * Utility to copy source object into target object including missing properties in target
 * If MMS (make, model, style) are objects with { code, description },
 * copy only their description to the target (string expected).
 */
export function DeepObjectCopy(target: any, source: any): any {
  Object.keys(source).forEach(sourceKey => {
    const value = source[sourceKey];

    // Handle Moment objects
    if (moment.isMoment(value)) {
      target[sourceKey] = moment(value).format('YYYY-MM-DD');
      return;
    }

    // Handle make/model/style objects → assign only description
    if (['make', 'model', 'style'].includes(sourceKey) && value) {
      if (typeof value === 'object' && 'description' in value) {
        target[sourceKey] = value.description;
        return;
      } else if (
        typeof value === 'string' &&
        !['makeCode', 'modelCode', 'styleCode'].includes(sourceKey)
      ) {
        target[sourceKey] = value;
        return;
      }
    }

    // Handle makeCode/modelCode/styleCode objects → assign only code
    if (
      ['makeCode', 'modelCode', 'styleCode'].includes(sourceKey) &&
      (source[sourceKey] || source[sourceKey.replace('Code', '')])
    ) {
      if (
        typeof source[sourceKey.replace('Code', '')] === 'object' &&
        'code' in source[sourceKey.replace('Code', '')]
      ) {
        target[sourceKey] = source[sourceKey.replace('Code', '')].code;
        return;
      } else if (value) {
        target[sourceKey] = value;
        return;
      } else if (typeof source[sourceKey.replace('Code', '')] === 'string') {
        target[sourceKey] = source[sourceKey.replace('Code', '')];
        return;
      }
    }

    // Handle nested objects and arrays normally
    if (value !== null && typeof value === 'object') {
      if (Array.isArray(value)) {
        target[sourceKey] = DeepObjectCopy(target[sourceKey] || [], value);
      } else {
        target[sourceKey] = DeepObjectCopy(target[sourceKey] || {}, value);
      }
    } else {
      // Assign primitive values directly
      target[sourceKey] = value;
    }
  });
  return target;
}

/**
 * Utility to copy source object into target object but only when the value is missing or the property is missing in the target
 */
export function DeepObjectCopyMissingValOrProp(target: any, source: any): any {
  Object.keys(source).forEach((sourceKey: string) => {
    if (target[sourceKey] === undefined || target[sourceKey] === null) {
      if (moment.isMoment(source[sourceKey])) {
        source[sourceKey] = moment(source[sourceKey]).format('YYYY-MM-DD');
      }
      if (source[sourceKey] !== null && typeof source[sourceKey] === 'object') {
        if (Array.isArray(source[sourceKey])) {
          target[sourceKey] = DeepObjectCopyMissingValOrProp(
            target[sourceKey] || [],
            source[sourceKey]
          );
        } else {
          target[sourceKey] = DeepObjectCopyMissingValOrProp(
            target[sourceKey] || {},
            source[sourceKey]
          );
        }
      } else {
        target[sourceKey] = source[sourceKey];
      }
    }
  });
  return target;
}

/**
 * Utility to copy source vales into target object only if property exists in target
 */
export function DeepCopyExistingProperties(source: any, target: any): any {
  for (const key in target) {
    if (target[key] !== null && typeof target[key] === 'object') {
      if (source[key]) {
        if (
          (key === eProductNames.MMP || key === eProductNames.VR) &&
          target[key].selected
        ) {
          const saveSelected = target[key].selected;
          DeepCopyExistingProperties(source[key], target[key]);
          target[key].selected = saveSelected;
        } else {
          DeepCopyExistingProperties(source[key], target[key]);
        }
      }
    } else if (typeof source === 'object' && key in source) {
      if (moment.isMoment(source[key])) {
        target[key] = moment(source[key]).format('YYYY-MM-DD');
      } else if (
        !(
          key === 'selected' &&
          (target.hasOwnProperty(eProductNames.MMP) ||
            target.hasOwnProperty(eProductNames.VR))
        )
      ) {
        target[key] = source[key];
      }
    }
  }
  return target;
}

/**
 * @func handles object keys date format to YYYY-MM-DD
 * @param object Object
 * @param keys: string[]
 * @param defaultDate date string
 * @returns Object
 */
export function dateFormater(
  object: Record<string, any>,
  keys: string[],
  defaultDate: string | null = null
): object {
  if (!!object && Array.isArray(keys)) {
    keys.forEach(key => {
      object[key] = object[key]
        ? moment(object[key]).format('YYYY-MM-DD')
        : defaultDate;
    });
  }
  return object;
}

/**
 * Returns the required error fields
 * @param unityDirectForm {FormGroup}
 * @returns invalidControlList {IErrorFieldObject[]}
 */
export function getRequiredFields(
  unityDirectForm: UntypedFormGroup
): IErrorFieldObject[] {
  const invalidControlList: IErrorFieldObject[] = [];
  Object.keys(unityDirectForm.controls).forEach(form => {
    const formGroup = unityDirectForm.get(form);
    let sideBarId: number;
    switch (form) {
      case 'loanCalculatorForm':
        sideBarId = 1;
        break;
      case 'borrowerInformationForm':
        sideBarId = 2;
        break;
      case 'vehicleInformationForm':
        sideBarId = 3;
        break;
    }

    Object.keys((formGroup as UntypedFormGroup).getRawValue()).forEach(
      controlName => {
        if (
          !formGroup.get(controlName)?.valid &&
          formGroup.get(controlName)?.status !== FORM_CONTROL_STATUS_DISABLED
        ) {
          const obj = {
            name: convertToTitleCase(controlName),
            formName: form,
            controlName: controlName,
            sideBarId: sideBarId
          };
          invalidControlList.push(obj);
        }
      }
    );
  });
  return invalidControlList;
}

/**
 * Formats the error fields with proper spaces to display on the UI
 * @param control {string}
 * @returns {string}
 */
function convertToTitleCase(control: string): string {
  return control
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, control => control.toUpperCase())
    .trim();
}

/**
 * Utility Method to calculate age by dob.
 * @param {string | null} dob
 * @returns {number}
 */
export function ageCalculator(dob: string | null): number {
  let age: number = 0;
  if (
    dob &&
    moment(dob, 'MM-DD-YYYY').isValid() &&
    moment(dob, 'MM-DD-YYYY').diff(moment(), 'seconds', true) < 0
  ) {
    age = Math.ceil(
      Math.abs(moment(dob, 'MM-DD-YYYY').diff(moment(), 'years', true))
    );
  }
  return age;
}

/**
 * Payment Protection age-eligibility outcome used to drive messaging/UI.
 * - BothEligible: both borrowers meet the age restriction, show all coverages.
 * - SingleEligible: only one borrower is eligible, show single coverages only.
 * - NoneEligible: neither borrower is eligible, only "Unprotected" remains.
 */
export enum PPIAgeEligibility {
  BothEligible = 'both',
  SingleEligible = 'single',
  NoneEligible = 'none'
}

export interface PPIAgeEligibilityResult {
  rates: PaymentProtectionRate[];
  eligibility: PPIAgeEligibility;
  // True only when a co-borrower exists and exactly one of the two applicants
  // is over the age limit. Drives the "one borrower does not meet..." message.
  // A lone eligible borrower (no co-borrower) is NOT a partial case.
  isPartialDueToIneligibleCoApplicant: boolean;
}

/**
 * Determines whether a Payment Protection coverage requires BOTH lives
 * (joint coverage) or applies to a single life only. A coverage is considered
 * joint if any component of its coverage name is a joint plan
 * (e.g. "Joint Life", "Single Life/Joint Disability",
 * "Joint Death/Joint Disability/Joint IU").
 * @param rate {PaymentProtectionRate}
 * @returns {boolean} true when the coverage is a joint (both-borrower) plan
 */
export function isJointPpiCoverage(rate: PaymentProtectionRate): boolean {
  return (rate?.coverageName ?? '').toLowerCase().includes('joint');
}

/**
 * Filters Payment Protection rates by borrower/co-borrower age against each
 * rate's maxLifeIssueAge, keeping single coverages when only one borrower is
 * eligible. The "Unprotected" (decline) option is always retained.
 *
 * Rules:
 * - Both borrowers eligible        -> keep all coverages.
 * - Sole eligible borrower (no co) -> keep all coverages (nothing to exclude).
 * - One of two eligible            -> keep single-life coverages + Unprotected,
 *                                     drop joint coverages.
 * - Neither eligible               -> keep Unprotected only.
 *
 * @param rates {PaymentProtectionRate[]}
 * @param borrowerCoBorrowerAge {IBorrowerCoBorrowerAge}
 * @returns {PPIAgeEligibilityResult}
 */
export function filterPpiRatesByAge(
  rates: PaymentProtectionRate[],
  borrowerCoBorrowerAge: IBorrowerCoBorrowerAge
): PPIAgeEligibilityResult {
  const { borrowerAge, coBorrowerAge } = borrowerCoBorrowerAge;

  // An age of 0 means no DOB was provided, i.e. that person does not exist
  // (ageCalculator returns 0 for a missing/empty DOB). A borrower that does
  // not exist should never be counted as eligible.
  const hasBorrower = borrowerAge > 0;
  const hasCoBorrower = coBorrowerAge > 0;

  // A rate's maxLifeIssueAge can differ per coverage; evaluate each borrower
  // that actually exists against the rate being considered.
  const borrowerEligibleFor = (rate: PaymentProtectionRate): boolean =>
    hasBorrower && borrowerAge <= rate.maxLifeIssueAge;
  const coBorrowerEligibleFor = (rate: PaymentProtectionRate): boolean =>
    hasCoBorrower && coBorrowerAge <= rate.maxLifeIssueAge;

  // "Both eligible" requires two people; with a single borrower only the
  // "one eligible" / "none eligible" outcomes are possible.
  const bothEligibleFor = (rate: PaymentProtectionRate): boolean =>
    hasBorrower &&
    hasCoBorrower &&
    borrowerEligibleFor(rate) &&
    coBorrowerEligibleFor(rate);
  const oneEligibleFor = (rate: PaymentProtectionRate): boolean =>
    borrowerEligibleFor(rate) || coBorrowerEligibleFor(rate);

  // Eligibility is derived only from real coverages. The "Unprotected"
  // (decline) option carries a very high maxLifeIssueAge and would otherwise
  // make every scenario look "both eligible".
  const realCoverages = rates.filter(
    rate => rate?.coverageName !== CoverageName.Unprotected
  );
  const anyBothEligible = realCoverages.some(bothEligibleFor);
  const anyOneEligible = realCoverages.some(oneEligibleFor);

  // A lone eligible borrower (no co-borrower) see all coverages
  // — treat them as "both eligible".
  const isLoneEligibleBorrower =
    hasBorrower && !hasCoBorrower && anyOneEligible;

  let eligibility: PPIAgeEligibility;
  if (anyBothEligible || isLoneEligibleBorrower) {
    eligibility = PPIAgeEligibility.BothEligible;
  } else if (anyOneEligible) {
    eligibility = PPIAgeEligibility.SingleEligible;
  } else {
    eligibility = PPIAgeEligibility.NoneEligible;
  }

  // The partial "one borrower is not eligible" message only applies when there
  // are actually two applicants and exactly one of them is over the age limit.
  // A single eligible borrower (no co-borrower) is fully covered, so no message.
  const isPartialDueToIneligibleCoApplicant =
    hasBorrower &&
    hasCoBorrower &&
    eligibility === PPIAgeEligibility.SingleEligible;

  const filtered = rates.filter(rate => {
    // Always keep the decline/unprotected option.
    if (rate?.coverageName === CoverageName.Unprotected) {
      return true;
    }
    if (eligibility === PPIAgeEligibility.BothEligible) {
      // Two eligible applicants must both qualify; a lone eligible borrower
      // qualifies on their own (no co-applicant to exclude).
      return hasCoBorrower ? bothEligibleFor(rate) : oneEligibleFor(rate);
    }
    if (eligibility === PPIAgeEligibility.SingleEligible) {
      // Only single-life coverages the eligible borrower qualifies for.
      return !isJointPpiCoverage(rate) && oneEligibleFor(rate);
    }
    // NoneEligible: drop every real coverage, keeping only Unprotected above.
    return false;
  });

  return { rates: filtered, eligibility, isPartialDueToIneligibleCoApplicant };
}

/**
 * Formats Zip code
 * @param zipCode {string}
 * @returns zipCode {string}
 */
export function formatZipCode(zipCode: string): string {
  if (zipCode?.includes('-')) {
    zipCode = zipCode.replace('-', '');
  }
  if (zipCode?.length > 5 && !zipCode?.includes('-')) {
    zipCode = zipCode.slice(0, 5) + '-' + zipCode.slice(5);
  }
  return zipCode;
}

/**
 * Utility Method to check for error code and message and decide if it should be ignored.
 * @param err string
 * @returns boolean
 */
export function isIgnoreError(err: string): boolean {
  // List of error codes and possible err strings that could be ignored (as in should not navigate to Error page)
  // If error code array is empty, ignore the error
  const errorIgnoreObj = {
    '400': [
      'Other Collaterals is not available with products selected.',
      'Odometer is required for MMP'
    ],
    '503': []
  };
  const truncatedErr = err.replace(/Code: /gi, '');
  const errCodeAndBlurbArr = truncatedErr.split('|');
  if (
    !errCodeAndBlurbArr.length ||
    errorIgnoreObj[errCodeAndBlurbArr[0].trim()] === undefined
  ) {
    return false;
  }
  if (
    errCodeAndBlurbArr[1].trim().includes('Vin is invalid for OtherCollateral')
  ) {
    return true;
  }
  if (!errorIgnoreObj[errCodeAndBlurbArr[0].trim()].length) {
    return true;
  } else {
    return (
      errorIgnoreObj[errCodeAndBlurbArr[0].trim()]
        .map((x: string) => x.toLocaleLowerCase())
        .indexOf(errCodeAndBlurbArr[1].trim().toLocaleLowerCase()) !== -1
    );
  }
}

/**
 * Utility to format interst rate.
 * Check if integer, fix decimal point.
 * @param rate {number}
 * @returns {number}
 */
export function formatInterestRate(rate: number): number {
  const tempInterestRate = Number(
    parseFloat((rate / 100).toString()).toFixed(6)
  );
  return Number.isInteger(tempInterestRate)
    ? tempInterestRate / 100
    : tempInterestRate;
}

/**
 * Method to change width of portal header depending on flag.
 * For example, Billing page needs a wider layout
 * @param isMakePortalHeaderLarge {boolean}
 */
export function changePortalHeaderWidth(
  isMakePortalHeaderLarge?: boolean
): void {
  setTimeout(() => {
    if (isMakePortalHeaderLarge) {
      //For pages like Billing, make layout wider.
      const mainHeader = document.querySelector('.main-header');
      const container = mainHeader?.querySelector('.container');
      if (container) {
        container.classList.remove('container');
        container.classList.add('container-large');
      }
    } else {
      //For pages other than Billing, make layout narrower.
      const mainHeader = document.querySelector('.main-header');
      const container = mainHeader?.querySelector('.container-large');
      if (container) {
        container.classList.remove('container-large');
        container.classList.add('container');
      }
    }
  }, 500);
}

/**
 * Utility function to calculate Loan To Value from Amount financed and Vehicle Value
 * @param totalAmountFinanced {number}
 * @param vehicleValue {string}
 * @returns {string}
 */
export function loanToValue(
  totalAmountFinanced: number,
  vehicleValue: string
): string {
  return ((totalAmountFinanced / Number(vehicleValue)) * 100).toFixed(2);
}

/**
 * Utility to check if two string arrays have the same values
 * @param arr1 {string[]} array of strings
 * @param arr2 {string[]} array of strings
 * @returns {boolean} true if array vales are same
 */
export function areArraysEqual(arr1: string[], arr2: string[]): boolean {
  if (!arr1 || !arr2) return false;
  if (arr1?.length !== arr2?.length) return false;

  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();

  return sorted1.every((val, index) => val === sorted2[index]);
}

/**
 * Decodes JWT
 * @param token {string}
 * @returns { Record<string, unknown> | null}
 */
export function decodeJwt(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Extracts user roles from decoded token
 * @param claims {any}
 * @returns {string[]}
 */
export function extractRoles(claims: any): string[] {
  const roles: string[] = [];

  if (Array.isArray(claims?.roles)) roles.push(...claims.roles);

  if (typeof claims?.role === 'string') roles.push(claims.role);
  if (Array.isArray(claims?.role)) roles.push(...claims.role);

  if (Array.isArray(claims?.realm_access?.roles))
    roles.push(...claims.realm_access.roles);

  const ra = claims?.resource_access;
  if (ra && typeof ra === 'object') {
    Object.keys(ra).forEach(client => {
      const r = ra?.[client]?.roles;
      if (Array.isArray(r))
        roles.push(...r.map((x: string) => `${client}:${x}`));
    });
  }

  return Array.from(new Set(roles)).filter(Boolean);
}

/**
 * Take in 2 objects - source and original.
 * takes each property of source and checks the value for that property in original. calls itself for nested objects
 * returns true if something has changed.
 * @param source  - the new object
 * @param original - the original copy
 * @returns {boolean}
 */
export function hasObjectChanged(current: any, original: any): boolean {
  return !isEqualByOriginal(current, original);
}

function isEqualByOriginal(current: any, original: any): boolean {
  if (Object.is(current, original)) {
    return true;
  }

  if (Array.isArray(original)) {
    if (!Array.isArray(current)) {
      return false;
    }

    if (current.length !== original.length) {
      return false;
    }

    return original.every((item, index) =>
      isEqualByOriginal(current[index], item)
    );
  }

  if (isObject(original)) {
    if (!isObject(current)) {
      return false;
    }

    return Object.keys(original).every(originalKey => {
      const currentKey = getCurrentKey(originalKey);

      return isEqualByOriginal(current[currentKey], original[originalKey]);
    });
  }

  return false;
}

function getCurrentKey(originalKey: string): string {
  const keyMap: Record<string, string> = {
    selected: 'isSelected'
  };

  return keyMap[originalKey] ?? originalKey;
}

function isObject(value: any): boolean {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
